// Student-inactivity rule for the AI Interviewer.
//
// The backend owns the numbers (backend/services/interview_inactivity.py) and
// sends them in the /start response; the defaults below only cover an older
// payload. Keeping the state machine here - separate from the React component -
// is what makes the 2-minute rule testable without a live avatar call, so a
// future refactor of the modal cannot quietly delete the rule.

export const DEFAULT_INACTIVITY = Object.freeze({
  warningSeconds: 120,
  terminationSeconds: 150,
});

// How long after the last agent transcript line the agent is still considered to
// be speaking. The inactivity clock is paused for that window: a long question
// must never be charged to the student as silence.
export const AI_TURN_GRACE_MS = 3000;

export const resolveInactivityConfig = (raw) => {
  const warning = Number(raw?.warningSeconds);
  const termination = Number(raw?.terminationSeconds);
  const warningSeconds = Number.isFinite(warning) && warning > 0
    ? warning
    : DEFAULT_INACTIVITY.warningSeconds;
  const terminationSeconds = Number.isFinite(termination) && termination > warningSeconds
    ? termination
    : Math.max(DEFAULT_INACTIVITY.terminationSeconds, warningSeconds + 1);
  return { warningSeconds, terminationSeconds };
};

export const createInactivityState = (config = DEFAULT_INACTIVITY) => {
  const { warningSeconds, terminationSeconds } = resolveInactivityConfig(config);
  return {
    elapsedMs: 0,
    warned: false,
    terminated: false,
    warningMs: warningSeconds * 1000,
    terminationMs: terminationSeconds * 1000,
    warn: false,
    terminate: false,
  };
};

// Advance the student's silent time. Callers simply do not call this while the
// agent is speaking, so pausing needs no special case in here.
export const advanceInactivity = (state, deltaMs) => {
  const current = state || createInactivityState();
  if (current.terminated) {
    return { ...current, warn: false, terminate: false };
  }
  const delta = Number.isFinite(deltaMs) && deltaMs > 0 ? deltaMs : 0;
  const elapsedMs = current.elapsedMs + delta;
  const warn = !current.warned && elapsedMs >= current.warningMs;
  const terminate = elapsedMs >= current.terminationMs;
  return {
    ...current,
    elapsedMs,
    warned: current.warned || warn,
    terminated: terminate,
    warn,
    terminate,
  };
};

// A real answer is the only thing that resets the clock.
export const resetInactivity = (state) => {
  const current = state || createInactivityState();
  return { ...current, elapsedMs: 0, warned: false, terminated: false, warn: false, terminate: false };
};

export const inactivityCountdown = (state) => {
  if (!state) return 0;
  return Math.max(0, Math.ceil((state.terminationMs - state.elapsedMs) / 1000));
};
